import React, { useEffect, useState, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from './firebase';
import { apiHeaders } from './utils/api';
import Layout from './components/Layout';
import Login from './pages/Login';
import MesNewsletters from './pages/MesNewsletters';
import NewsletterDetail from './pages/NewsletterDetail';
import MesAlertes from './pages/MesAlertes';
import Favoris from './pages/Favoris';
import MonProfil from './pages/MonProfil';
import Abonnement from './pages/Abonnement';
import Contact from './pages/Contact';
import AlertesClients from './pages/AlertesClients';
import Onboarding from './pages/Onboarding';
import CGU from './pages/CGU';
import Confidentialite from './pages/Confidentialite';

const API = 'https://aglomera.com/api';

// ── Plan Context ──────────────────────────────────────────────────────────────
export type Plan = 'gratuit' | 'pro' | 'premium';

interface PlanContextValue {
  plan: Plan;
  setPlan: (p: Plan) => void;
  loadingPlan: boolean;
}

export const PlanContext = createContext<PlanContextValue>({
  plan: 'gratuit',
  setPlan: () => {},
  loadingPlan: true,
});

export const usePlan = () => useContext(PlanContext);

const LoadingSpinner: React.FC = () => (
  <div className="flex h-screen items-center justify-center">
    <div
      className="w-8 h-8 rounded-full border-2 animate-spin"
      style={{ borderColor: '#2a52a8', borderTopColor: 'transparent' }}
    />
  </div>
);

/**
 * OnboardingGuard : vérifie si le profil du nouvel utilisateur est vide.
 * Si statut_candidat est null ET postes_vises est vide → redirige vers /onboarding.
 * Ne s'applique pas à la route /onboarding elle-même.
 */
const OnboardingGuard: React.FC<{ user: User; children: React.ReactNode }> = ({ user, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (location.pathname === '/onboarding') {
      setChecked(true);
      return;
    }
    let cancelled = false;
    const check = async () => {
      try {
        const res = await fetch(`${API}/profil/`, {
          headers: await apiHeaders(),
        });
        if (!res.ok) { setChecked(true); return; }
        const d = await res.json();
        const isEmpty =
          !Array.isArray(d.postes_vises) || d.postes_vises.length === 0;
        if (!cancelled && isEmpty) {
          navigate('/onboarding', { replace: true });
        }
      } catch {
        // ignore — on ne bloque pas si l'API est indisponible
      } finally {
        if (!cancelled) setChecked(true);
      }
    };
    check();
    return () => { cancelled = true; };
  }, [user.uid, location.pathname, navigate]);

  if (!checked && location.pathname !== '/onboarding') return <LoadingSpinner />;
  return <>{children}</>;
};

const PrivateRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => {
  const [user, setUser] = useState<User | null | 'loading'>('loading');

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => setUser(u));
    return unsub;
  }, []);

  if (user === 'loading') return <LoadingSpinner />;

  return user ? element : <Navigate to="/login" replace />;
};


// ── Provider plan chargé depuis l'API ─────────────────────────────────────────
const PlanProvider: React.FC<{ user: User; children: React.ReactNode }> = ({ user, children }) => {
  const [plan, setPlan] = useState<Plan>('gratuit');
  const [loadingPlan, setLoadingPlan] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      fetch(`${API}/profil/`, { headers: await apiHeaders() })
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (!cancelled && d?.plan) {
            setPlan(d.plan as Plan);
          }
        })
        .catch(() => {})
        .finally(() => { if (!cancelled) setLoadingPlan(false); });
    })();
    return () => { cancelled = true; };
  }, [user.uid]);

  return (
    <PlanContext.Provider value={{ plan, setPlan, loadingPlan }}>
      {children}
    </PlanContext.Provider>
  );
};

// Wrapper PrivateGuarded qui inclut le PlanProvider
const PrivateGuardedWithPlan: React.FC<{ element: React.ReactElement }> = ({ element }) => {
  const [user, setUser] = useState<User | null | 'loading'>('loading');

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => setUser(u));
    return unsub;
  }, []);

  if (user === 'loading') return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <PlanProvider user={user}>
      <OnboardingGuard user={user}>
        {element}
      </OnboardingGuard>
    </PlanProvider>
  );
};

const App: React.FC = () => (
  <BrowserRouter basename="/app">
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/cgu" element={<CGU />} />
      <Route path="/confidentialite" element={<Confidentialite />} />
      <Route path="/" element={<Navigate to="/alertes" replace />} />
      <Route path="/alertes" element={<PrivateGuardedWithPlan element={<Layout><MesAlertes /></Layout>} />} />
      <Route path="/onboarding" element={<PrivateRoute element={<Onboarding />} />} />
      <Route path="/newsletters"      element={<PrivateGuardedWithPlan element={<Layout><MesNewsletters /></Layout>} />} />
      <Route path="/newsletters/:slug" element={<PrivateGuardedWithPlan element={<Layout><NewsletterDetail /></Layout>} />} />
      <Route path="/historique" element={<Navigate to="/favoris" replace />} />
      <Route path="/favoris"    element={<PrivateGuardedWithPlan element={<Layout><Favoris /></Layout>} />} />
      <Route path="/profil"     element={<PrivateGuardedWithPlan element={<Layout><MonProfil /></Layout>} />} />
      <Route path="/abonnement" element={<PrivateGuardedWithPlan element={<Layout><Abonnement /></Layout>} />} />
      <Route path="/contact"    element={<PrivateGuardedWithPlan element={<Layout><Contact /></Layout>} />} />
      <Route path="/alertes-clients"  element={<PrivateGuardedWithPlan element={<Layout><AlertesClients /></Layout>} />} />
    </Routes>
  </BrowserRouter>
);

export default App;
